import {
  addDays,
  differenceInDays,
  differenceInMinutes,
  differenceInMonths,
  format,
  formatDuration,
  intervalToDuration,
  isBefore,
  isValid,
  isWithinInterval,
  max,
  min,
  parse as parseDate,
  parseISO
} from "date-fns";
import { utcToZonedTime, zonedTimeToUtc } from "date-fns-tz";
import { decodeHTML } from "entities";
import { saveAs } from "file-saver";
import * as htmlToImage from "html-to-image";
import {
  camelCase,
  isNil,
  omit,
  omitBy,
  snakeCase,
  truncate,
  values
} from "lodash";
import { parse, unparse } from "papaparse";

import { NON_BREAKABLE_SPACE } from "constants/common";
import { StaffMemberNameParts } from "services/types/staff.type";
import { viteEnv } from "utils/vite-env.util";

import { TITLES } from "../constants";

const DATE_MASK = "MM/dd/yyyy";

export const extractBackendErrorStringMessages = (
  response: any,
  result: Set<string>,
  includeFieldName: boolean = false,
  fieldName: string = ""
) => {
  if (typeof response !== "object") return;
  for (let propName in response) {
    if (
      response.hasOwnProperty(propName) &&
      response[propName] &&
      typeof response[propName] === "object"
    ) {
      extractBackendErrorStringMessages(
        response[propName],
        result,
        includeFieldName,
        Number.isNaN(Number(propName)) ? propName : fieldName
      );
    } else {
      if (
        propName === "message" &&
        typeof response[propName] === "string" &&
        !result.has(response[propName])
      ) {
        const message = `${includeFieldName ? `${fieldName}: ` : ""}${
          response[propName]
        }`;
        result.add(message);
      }
    }
  }
};

export const hasStatusTransitionError = (errorMessage: string): boolean => {
  const regex = /Transition from (.+) to (.+) is not allowed/;
  return Boolean(errorMessage.match(regex));
};

export const isBlank = (
  value?: string | number | null | boolean | object
): boolean => {
  return (
    value === null ||
    value === undefined ||
    (typeof value === "object" && isBlankObject(value)) ||
    String(value).trim() === ""
  );
};

export const getFirstNonBlank = (
  values: Array<string | number | null | boolean | object | undefined>
) => {
  for (const value of values) {
    if (!isBlank(value)) {
      return value;
    }
  }
  return undefined;
};

export const getValueOrDefault = (
  value: string | number | null | boolean | object | undefined,
  defaultValue: string | number | null | boolean | object | undefined
) => (isBlank(value) ? defaultValue : value);

export const isNotBlank = (
  value?: string | number | null | boolean
): boolean => {
  return !isBlank(value);
};

export const isBlankObject = (value: any): boolean => {
  if (value === null || value === undefined) return true;

  if (Array.isArray(value) && (!value.length || !value.some(isNotBlank)))
    return true;

  return !values(value).some(isNotBlank);
};

export const isWithin24Hours = (dateStr: string): boolean => {
  const date = new Date(dateStr);
  const now = new Date();
  const twentyFourHoursAgo = new Date(now.getTime() - 24 * 60 * 60 * 1000);
  return isWithinInterval(date, {
    start: twentyFourHoursAgo,
    end: now
  });
};

export const isInThePast = (dateStr: string): boolean => {
  const date = parseISO(dateStr);
  const now = new Date();
  return isBefore(date, now);
};

export const getDateTime = (date: Date, time: Date) =>
  new Date(`${format(date, "yyyy-MM-dd")} ${format(time, "HH:mm")}`);

export const formatDateToDateAndTime = (date: Date) =>
  `${format(date, "MM/dd/yy")} ${format(date, "hh:mmaaaaa'm'")}`;

export const formatDateInTimezone = (
  date: Date | string | number,
  timezone = Intl.DateTimeFormat().resolvedOptions().timeZone
) => {
  return zonedTimeToUtc(date, timezone);
};

export const convertDateInTimezoneToCurrent = (
  date: Date | string | number,
  timezone = Intl.DateTimeFormat().resolvedOptions().timeZone
) => {
  const currentTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  return utcToZonedTime(zonedTimeToUtc(date, currentTimeZone), timezone);
};

export const convertDateFromCurrentTimezoneToTimezone = (
  date: Date | string | number,
  newTimezone: string = Intl.DateTimeFormat().resolvedOptions().timeZone
) => {
  const currentTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const utcDate = zonedTimeToUtc(date, newTimezone);
  const zonedDate = utcToZonedTime(utcDate, currentTimeZone);
  return zonedDate;
};

export const getDaysInDateTimeInterval = (start: Date, end: Date) => {
  return Math.ceil(Math.abs(differenceInMinutes(end, start) / 1440)); //1440 = 24 hours * 60 minutes
};

export const getDatesInDateTimeInterval = (start: Date, end: Date) => {
  const days = getDaysInDateTimeInterval(start, end);
  const length = days === 0 ? 1 : days;
  const result: Date[] = [];
  for (let i = 0; i < length; i++) {
    result.push(addDays(start, i));
  }
  return result;
};

export function formatDateOnlyValue(
  dateString: string | null | undefined,
  dateFormat: string = DATE_MASK,
  emptyValue: string = ""
) {
  if (dateString == null || String(dateString).trim() === "") {
    return emptyValue;
  }
  try {
    let date = parseDate(dateString, "yyyy-MM-dd", new Date());
    if (!isValid(date)) {
      date = new Date(dateString);
    }
    if (!isValid(date)) {
      return emptyValue;
    }
    return format(date, dateFormat);
  } catch {
    return emptyValue;
  }
}

export function formatBackendDateOnly(
  dateOnly: string = new Date().toISOString(),
  dateFormat: string = "yyyy-MM-dd",
  emptyValue: string = ""
) {
  if (dateOnly == null || String(dateOnly).trim() === "") {
    return emptyValue;
  }
  try {
    const dt = new Date(dateOnly);
    if (!isValid(dt)) {
      return emptyValue;
    }
    const year = dt.getFullYear();
    if (year < 1900 || year > 2100) return emptyValue;
    const dtOnlyDate = new Date(
      dt.valueOf() + dt.getTimezoneOffset() * 60 * 1000
    );
    if (!isValid(dtOnlyDate)) {
      return emptyValue;
    }
    return format(dtOnlyDate, dateFormat);
  } catch {
    return emptyValue;
  }
}

export function formatLocalDateOnly(
  dateOnly: string = new Date().toISOString(),
  dateFormat: string = "yyyy-MM-dd"
) {
  const dt = new Date(dateOnly);
  if (isNaN(dt.getTime())) return "";

  // Extract UTC date components to preserve the intended date
  const year = dt.getUTCFullYear();
  const month = dt.getUTCMonth();
  const date = dt.getUTCDate();

  const localDate = new Date(year, month, date);
  return format(localDate, dateFormat);
}

export function parseBackendDateOnly(
  dateString: string = new Date().toISOString()
) {
  return parseDate(dateString, "yyyy-MM-dd", new Date());
}

export function strToDuration(duration: string) {
  const [hours, minutes, seconds] = duration?.split(":");
  return {
    hours: Number.parseInt(hours),
    minutes: Number.parseInt(minutes),
    seconds: Number.parseInt(seconds)
  } as Duration;
}

export function formatPhoneNumber(
  phoneNumber: string | undefined | null,
  format: string = "dash"
) {
  if (!phoneNumber) return "";
  const cleaned = String(phoneNumber ?? "").replace(/\D/g, "");
  if (!cleaned) return "";
  const match = cleaned.match(/^(\d{3})(\d{3})(\d{4})$/);

  if (match) {
    if (format === "dash") {
      return `${match[1]}-${match[2]}-${match[3]}`;
    } else if (format === "parenthesis") {
      return `(${match[1]}) ${match[2]}-${match[3]}`;
    }
  }

  return null;
}

export function formatBirthdate(birthdate?: string | null) {
  if (!birthdate) return null;

  const date = parseDate(birthdate, "yyyy-MM-dd", new Date());

  return isValid(date) ? date : null;
}

export const formatBirthdateAsString = (
  birthdate?: string | null | Date,
  outputFormat = "MMM dd, yyyy"
) => {
  let date: Date;

  if (!birthdate) return "";
  if (birthdate instanceof Date) {
    date = birthdate;
  } else {
    if (birthdate) {
      date = parseDate(birthdate, "yyyy-MM-dd", new Date());
      if (!isValid(date)) {
        date = new Date(birthdate);
      }
    } else {
      return "";
    }
  }
  return format(date, outputFormat).replaceAll(" ", NON_BREAKABLE_SPACE);
};

export const formatMoneyValue = (value: number, isNegative?: boolean) => {
  return `${isNegative && value ? "-" : ""}${formatMoney(value, 2)}`;
};

export const formatMoney = (
  value: number,
  minimumFractionDigits?: number,
  maximumFractionDigits: number = 2,
  showSign: boolean = false,
  symbol: string = "$",
  direction: "before" | "after" = "before"
) => {
  const hasDecimals = value % 1 !== 0;
  const minDigits = minimumFractionDigits ?? (hasDecimals ? 2 : 0);
  const sign = showSign && value < 0 ? "-" : "";
  return `${sign}${direction === "before" ? symbol : ""}${Math.abs(
    value
  ).toLocaleString("en-US", {
    minimumFractionDigits: minDigits,
    maximumFractionDigits
  })}${direction === "after" ? symbol : ""}`;
};

export const calculatePercent = (
  part?: string,
  full?: string,
  max: number | undefined = undefined
) => {
  let result =
    part && Number(part || "0") > 0
      ? Math.round((Number(part || "0") / Number(full)) * 100 * 100) / 100
      : 0;

  if (max && result > max) return max;
  return result;
};

export const formatPercent = (
  value: number | "N/A",
  minimumFractionDigits: number = 0,
  maximumFractionDigits: number = 2
) =>
  value === "N/A"
    ? "N/A"
    : `${Math.abs(value).toLocaleString("en-US", {
        minimumFractionDigits,
        maximumFractionDigits
      })}%`;

export const formatMoneyWithNegativeValue = (
  value: number,
  minimumFractionDigits?: number,
  maximumFractionDigits: number = 2
) => {
  const hasDecimals = value % 1 !== 0;
  const minDigits = minimumFractionDigits ?? (hasDecimals ? 2 : 0);
  const stringValue = Math.abs(value).toLocaleString("en-US", {
    minimumFractionDigits: minDigits,
    maximumFractionDigits
  });

  return value >= 0 ? `$${stringValue}` : `($${stringValue})`;
};

export const normalizeFloatingNumberCorrection = (value: number) =>
  Math.round(value * 100) / 100;

type PatientNameParts = {
  firstName: string;
  middleName?: string | null;
  preferredName?: string | null;
  lastName: string;
  title?: number | null;
  suffix?: string;
};

export const getFullName = ({
  firstName,
  middleName,
  preferredName,
  lastName,
  title,
  suffix
}: PatientNameParts) => {
  return [
    [
      TITLES.getFromCode(title),
      firstName,
      preferredName ? `(${preferredName})` : "",
      middleName,
      lastName
    ]
      .filter((namePart) => Boolean(namePart))
      .join(" "),
    suffix ? `, ${suffix}` : ""
  ].join("");
};

export function formatStaffMemberFullName<T>(
  staffMember: T & StaffMemberNameParts
): T {
  return {
    ...staffMember,
    fullName: getFullName({
      firstName: staffMember?.first_name ?? "",
      lastName: staffMember?.last_name ?? "",
      title: staffMember?.title,
      suffix: staffMember?.suffix
    })
  };
}

export function formatValidQueryParamUrlCommaSeparated(
  urlParams: {
    [param: string]: any;
  },
  addQuestionMark: boolean = false
) {
  const result = Object.keys(urlParams)
    .filter(
      (key: string) =>
        urlParams[key] !== undefined &&
        urlParams[key] !== null &&
        urlParams[key] !== "" &&
        urlParams[key]?.length !== 0
    )
    .map((key: string) => {
      if (Array.isArray(urlParams[key])) {
        return `${key}=` + urlParams[key].join(",");
      } else {
        return `${key}=${urlParams[key]}`;
      }
    })
    .join("&");

  if (addQuestionMark) {
    return isBlank(result) ? "?" : `?${result}`;
  }
  return result;
}

export function formatValidQueryParamUrl(urlParams: { [param: string]: any }) {
  return Object.keys(urlParams)
    .filter(
      (key: string) => urlParams[key] !== undefined && urlParams[key] !== null
    )
    .map((key: string) => {
      if (Array.isArray(urlParams[key])) {
        return urlParams[key]
          .map((value: string) => `${key}=${value}`)
          .join("&");
      } else {
        return `${key}=${urlParams[key]}`;
      }
    })
    .join("&");
}

export function getParentPath(pathname: string, stepsBack: number = 1) {
  const paths = pathname.split("/").filter((pathPart) => Boolean(pathPart));

  if (paths.length === 0) {
    return pathname;
  }
  return "/" + paths.slice(0, -stepsBack).join("/");
}

/**
 * Extracts the current route path from the URL without query parameters.
 * Ensures the path starts with "/" and doesn't end with "/" (except if it's just "/").
 *
 * @param url - Optional URL string. If not provided, uses window.location.href
 * @returns The normalized path (e.g., "/patients/7" or "/")
 *
 * @example
 * getCurrentRoutePath() // from http://localhost:3000/patients/7/ -> "/patients/7"
 * getCurrentRoutePath() // from http://localhost:3000/ -> "/"
 * getCurrentRoutePath("http://localhost:3000/patients/7?query=test") // -> "/patients/7"
 */
export function getCurrentRoutePath(url?: string): string {
  const urlToProcess = url || window.location.href;
  const urlObj = new URL(urlToProcess);
  let pathname = urlObj.pathname;

  // Remove trailing slash, except if pathname is just "/"
  if (pathname !== "/" && pathname.endsWith("/")) {
    pathname = pathname.slice(0, -1);
  }

  // Ensure it starts with "/"
  if (!pathname.startsWith("/")) {
    pathname = "/" + pathname;
  }

  return pathname;
}

export function getFieldName<FieldName extends string>(
  name: string,
  fieldName?: string
): FieldName {
  if (fieldName === undefined) return name as FieldName;
  return `${name}.${fieldName}` as FieldName;
}

type SimpleValueType = string | number | boolean;
type FlatObjectType = {
  [key: string]: SimpleValueType | SimpleValueType[];
};
type ComplexObjectType = {
  [key: string]:
    | SimpleValueType
    | ComplexObjectType
    | FlatObjectType
    | SimpleValueType[];
};
export type ObjectConversionInputType =
  | ComplexObjectType
  | SimpleValueType
  | SimpleValueType[];
export function camelCaseKeysToUnderscore(obj: ObjectConversionInputType) {
  if (typeof obj !== "object" || Array.isArray(obj)) return obj;

  for (const camelCaseName in obj) {
    // Camel to underscore
    const underscoreName = snakeCase(camelCaseName);

    // Only process if names are different
    if (underscoreName !== camelCaseName) {
      // Check for the old property name to avoid a ReferenceError in strict mode.
      if (obj.hasOwnProperty(camelCaseName)) {
        obj[underscoreName] = obj[camelCaseName];
        delete obj[camelCaseName];
      }
    }

    // Recursion
    if (typeof obj[underscoreName] === "object") {
      obj[underscoreName] = camelCaseKeysToUnderscore(obj[underscoreName]);
    }
  }
  return obj;
}

export function underScoreKeysToCamelCase(obj: ObjectConversionInputType) {
  if (typeof obj !== "object" || Array.isArray(obj)) return obj;

  for (const underscoreName in obj) {
    // Camel to underscore
    const camalcaseName = camelCase(underscoreName);

    // Only process if names are different
    if (camalcaseName !== underscoreName) {
      // Check for the old property name to avoid a ReferenceError in strict mode.
      if (obj.hasOwnProperty(underscoreName)) {
        obj[camalcaseName] = obj[underscoreName];
        delete obj[underscoreName];
      }
    }

    // Recursion
    if (typeof obj[camalcaseName] === "object") {
      obj[camalcaseName] = underScoreKeysToCamelCase(obj[camalcaseName]);
    }
  }
  return obj;
}

export const convertHtmlToText = (html: string | null | undefined) => {
  if (typeof html !== "string") return "";
  return decodeHTML(
    html
      // Remove head tag and its content
      .replace(/<head\b[^<]*(?:(?!<\/head>)<[^<]*)*<\/head>/gi, "")
      // Remove xml tag and its content
      .replace(/<xml\b[^<]*(?:(?!<\/xml>)<[^<]*)*<\/xml>/gi, "")
      // Remove script tags and their content
      .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, "")
      // Remove style tags and their content (CSS)
      .replace(/<style\b[^<]*(?:(?!<\/style>)<[^<]*)*<\/style>/gi, "")
      // Convert br tags to newlines
      .replace(/<br\s*\/?>/gi, "\n")
      // Remove all remaining HTML tags
      .replace(/<[^>]+>/g, "")
      // Handle your custom SUNO syntax
      .replace(/!--SUNO:(.*?)--/g, "{{$1}}")
      // Clean up extra horizontal whitespace (preserve newlines from br tags)
      .replace(/[ \t]+/g, " ")
      .trim()
  );
};

export const getHtmlAsTextPreview = (
  html: string | undefined | null,
  placeholder: string = "",
  length: number = 160
) => {
  if (typeof html !== "string") return placeholder;

  const text = convertHtmlToText(html).trim();
  if (!text) return placeholder;

  return truncate(text, {
    length,
    omission: "…"
  });
};

export function getParsedJson(str?: string | null): any {
  if (str === null || str === undefined || str === "") return null;
  if (str.includes("{") || str.includes("[")) {
    try {
      const parsed = JSON.parse(str);
      return typeof parsed === "object" && parsed !== null ? parsed : null;
    } catch (e) {
      return null;
    }
  }
  return null;
}

export function isJson(str?: string | null): boolean {
  return Boolean(getParsedJson(str));
}

export async function dataUrlToFile(
  dataUrl: string,
  fileName: string
): Promise<File> {
  const res: Response = await fetch(dataUrl);
  const blob: Blob = await res.blob();
  return new File([blob], fileName, { type: "image/png" });
}

export const isValidDate = (value: string | undefined | null, mask: string) => {
  return isValid(parseDate(value ?? "", mask, new Date()));
};

export const getValidDateOrDefault = (value: any, defaultValue: Date) => {
  if (isValid(value)) return value as Date;
  return defaultValue;
};

export const formatResourceUrl = (url?: string, bustCache: boolean = false) => {
  if (!url || url.startsWith("blob:") || url.startsWith("data:")) return url;

  let formattedUrl: string;
  if (url.startsWith("/")) {
    formattedUrl = viteEnv.VITE_SUNO_API?.replace("/api", "").concat(url);
  } else {
    formattedUrl = url;
  }

  // Add cache busting parameter for PDF generation
  if (bustCache && formattedUrl) {
    const cacheParam = `${formattedUrl.includes("?") ? "&" : "?"}_cb=`;
    if (!formattedUrl.includes(cacheParam)) {
      formattedUrl += `${cacheParam}${Date.now()}`;
    }
  }

  return formattedUrl;
};

export const downloadCSVWithFormatter = (
  blob: Blob,
  fileName?: string,
  formatter?: (data: any[]) => any[]
) => {
  const reader = new FileReader();
  reader.onload = (event) => {
    const rawText = event.target?.result as string;
    const rawData = parse(rawText, { header: true, skipEmptyLines: true }).data;
    const formattedData = formatter ? formatter(rawData) : rawData;
    const csvData = unparse(formattedData, {
      quotes: true,
      quoteChar: '"',
      delimiter: ",",
      header: true
    });
    const csvBlob = new Blob([csvData], { type: "text/csv;charset=utf-8" });
    saveAs(csvBlob, fileName);
  };
  reader.readAsText(blob);
  return blob;
};

export function extractDecimalZeros(str: string) {
  const match = str.match(/\.(0+)?$/);
  return match ? match[0] : null;
}

export function isDateStringValid(date: any): boolean {
  const dt = typeof date === "string" ? new Date(date) : date;
  if (!isValid(dt) && !isValid(parseISO(dt))) return false;
  const year = (isValid(dt) ? dt : parseISO(dt)).getFullYear();
  return year >= 1900 && year <= 2100;
}

export function isDateRangeStringValid(fromDate: any, toDate: any) {
  return (
    isDateStringValid(fromDate) &&
    isDateStringValid(toDate) &&
    differenceInDays(new Date(toDate), new Date(fromDate)) >= 0
  );
}

export const nodeToJpeg = async (node: any, quality: number = 1) => {
  return htmlToImage.toJpeg(node, {
    backgroundColor: "#fff",
    quality,
    cacheBust: true,
    pixelRatio: 3,
    skipAutoScale: true,
    skipFonts: true
  });
};

export const nodeToPng = async (node: any) => {
  return htmlToImage.toPng(node, {
    backgroundColor: "#fff",
    cacheBust: true,
    pixelRatio: 3,
    skipAutoScale: true,
    skipFonts: true
  });
};
export const convertBlobUrlToFile = async (
  blob_url: string,
  fileName: string
) =>
  fetch(blob_url)
    .then((response) => response.blob())
    .then((blob) => {
      return new File([blob], fileName, { type: blob.type });
    });

export const isObject = (obj: any) => {
  return typeof obj === "object" && !Array.isArray(obj) && obj !== null;
};

const kilobyte: number = 1024;
const megabyte: number = kilobyte ** 2;
const gigabyte: number = kilobyte ** 3;

export const formatBytes = (byteSize: number, decimalPlaces = 0) => {
  if (byteSize < kilobyte) {
    return `${byteSize}B`;
  }
  if (byteSize < megabyte) {
    return `${(byteSize / kilobyte).toFixed(0)}KB`;
  }
  if (byteSize < gigabyte) {
    return `${(byteSize / megabyte).toFixed(decimalPlaces)}MB`;
  }

  return `${(byteSize / gigabyte).toFixed(decimalPlaces)}GB`;
};

export {
  calculateTaxRateValue,
  computeTaxAmount,
  computeTotalWithTax,
  roundTo,
  roundToTwo
} from "./math";

export function transformObjectPropertyValuesToStrings(obj: any) {
  return Object.keys(obj).reduce((acc, key) => {
    const value = obj[key];
    acc[key] =
      typeof value === "string" || typeof value === "number"
        ? String(value)
        : "";
    return acc;
  }, {} as any);
}

export function downloadBlob(blob: Blob, fileName: string) {
  const fileUrl = window.URL.createObjectURL(blob);
  const tempLink = document.createElement("a");
  tempLink.href = fileUrl;
  tempLink.setAttribute("download", fileName);
  tempLink.setAttribute("target", "_blank");
  document.body.appendChild(tempLink);
  tempLink.click();
  document.body.removeChild(tempLink);
}

export function formatDurationValue(duration: string) {
  const [hours, minutes] = duration.split(":").map(Number);
  const durationInterval = intervalToDuration({
    start: 0,
    end: hours * 3600 * 1000 + minutes * 60 * 1000
  });

  return formatDuration(durationInterval);
}

export const deepEqualIgnoreIsNull = (object1: any, object2: any) => {
  const keys1 = Object.keys(omitBy(object1, isNil));
  const keys2 = Object.keys(omitBy(object2, isNil));

  if (keys1.length !== keys2.length) {
    return false;
  }

  for (const key of keys1) {
    const value1 = object1[key];
    const value2 = object2[key];

    if (value1 === null && value2 === null) {
      return true;
    }

    if (value1 === undefined && value2 === undefined) {
      return true;
    }

    if (typeof value1 === "object" && typeof value2 === "object") {
      if (!deepEqualIgnoreIsNull(value1, value2)) {
        return false;
      }
    } else if (value1 !== value2) {
      return false;
    }
  }

  return true;
};

export function openFile(
  fileUrl: string,
  shouldPrint: boolean = false,
  filename?: string
) {
  const tempLink = document.createElement("a");
  tempLink.href = fileUrl;
  tempLink.setAttribute("target", "_blank");

  // Set download attribute with filename to ensure proper file type and download behavior
  if (filename) {
    tempLink.setAttribute("download", filename);
  }

  if (shouldPrint) {
    tempLink.addEventListener("click", (e) => {
      e.preventDefault(); // Prevent immediate navigation

      const printWindow = window.open(fileUrl, "_blank");

      if (printWindow) {
        printWindow.onload = () => {
          try {
            printWindow.print();
          } catch (error) {
            console.error("Printing failed:", error);
            // Fallback to just opening the file if printing fails
            printWindow.close();
            window.open(fileUrl, "_blank");
          }
        };
      }
    });
  }

  document.body.appendChild(tempLink);
  tempLink.click();
  document.body.removeChild(tempLink);
}

export const formatDaysDisplay = (
  duration: number | null | undefined
): string => {
  if (duration === null || duration === 0) return "";
  return duration === 1 ? "1 day" : `${duration} days`;
};

export const getPathNameWithoutEndingSlash = (pathName: string) => {
  return pathName.endsWith("/") ? pathName.slice(0, -1) : pathName;
};

export const getAgeInMonthsOrYears = (
  age_years: string | number | null | undefined,
  birthdate: string | null | undefined
) => {
  const patientDateOfBirth = birthdate
    ? format(
        typeof birthdate === "string"
          ? parseDate(birthdate, "yyyy-MM-dd", new Date())
          : new Date(birthdate),
        DATE_MASK
      )
    : "";

  const monthsAge =
    !Number(age_years) && patientDateOfBirth
      ? differenceInMonths(new Date(), new Date(patientDateOfBirth))
      : "";

  const monthsAgeText = monthsAge
    ? `${monthsAge} month${monthsAge !== 1 ? "s" : ""}`
    : "";
  return monthsAgeText ? monthsAgeText : age_years;
};

export function getDateDifferenceInHumanFormat(
  startDate: Date,
  endDate: Date,
  postfix?: string
): string {
  const duration = intervalToDuration({
    start: min([startDate, endDate]),
    end: max([startDate, endDate])
  });

  if (
    !duration.years &&
    !duration.weeks &&
    !duration.months &&
    !duration.days &&
    !duration.hours &&
    !duration.minutes &&
    (!duration.seconds || duration.seconds < 60)
  ) {
    return "now";
  }

  return `${formatDuration(omit(duration, "seconds"))
    .replace(/minute[s]?/i, "min")
    .replace(/ hour[s]?/i, "h")}${postfix !== undefined ? " " + postfix : ""}`;
}

// `sanitizeHTML` was extracted to its own module back when `common.ts` read Vite's
// `import.meta` directly (unparseable by Jest; env access now goes through
// utils/vite-env.util). The re-export below preserves the existing
// `import { sanitizeHTML } from "utils/common"` call sites.
export { sanitizeHTML } from "./sanitize-html.util";

export const getSelectedIds = (ids: Record<number, boolean>) => {
  return Object.entries(ids || {})
    .filter(([id, selected]) => selected)
    .map(([id]) => String(id));
};

export const fileToBase64 = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => {
      // Returns full data URL including MIME type (e.g. "data:image/png;base64,ABC123...")
      resolve(reader.result as string);
    };
    reader.onerror = (error) => reject(error);
  });
};

export const base64ToFile = (
  dataUrl: string,
  filename: string
): File | undefined => {
  // Parse the data URL to extract MIME type and base64 data
  const matches = dataUrl.match(/^data:(.+?);base64,(.+)$/);
  if (!matches || matches.length !== 3) {
    console.error(
      "Invalid data URL format. Expected format: data:<mime-type>;base64,<data>"
    );
    return undefined;
  }

  const mimeType = matches[1];
  const base64Data = matches[2];

  // Convert Base64 to binary using atob
  const byteCharacters = atob(base64Data);
  const byteArrays = new Uint8Array(byteCharacters.length);

  for (let i = 0; i < byteCharacters.length; i++) {
    byteArrays[i] = byteCharacters.charCodeAt(i);
  }

  // Create and return the File object
  const blob = new Blob([byteArrays], { type: mimeType });
  return new File([blob], filename, { type: mimeType });
};

export const urlToBase64 = async (
  url: string,
  retries = 1,
  timeoutMs?: number
): Promise<string | null> => {
  if (!url) return null;
  if (url.startsWith("data:") || url.startsWith("blob:")) return url;

  try {
    const controller = new AbortController();
    const timeoutId =
      timeoutMs !== undefined
        ? setTimeout(() => controller.abort(), timeoutMs)
        : undefined;

    const response = await fetch(url, { signal: controller.signal });
    if (timeoutId) clearTimeout(timeoutId);
    if (!response.ok) return null;

    const blob = await response.blob();
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result as string);
      reader.onerror = () => resolve(null);
      reader.readAsDataURL(blob);
    });
  } catch (error) {
    if (retries > 0) {
      await new Promise((r) => setTimeout(r, 1000));
      return urlToBase64(url, retries - 1, timeoutMs);
    }
    console.error("Error converting URL to base64:", error);
    return null;
  }
};

export async function blobToUint8Array(blob: Blob) {
  const arrayBuffer = await blob.arrayBuffer();
  return new Uint8Array(arrayBuffer);
}

export function isDefaultPhoneNumber(phone: string): boolean {
  if (!phone) return false;

  const digits = phone.replace(/\D/g, "");

  return digits.length > 0 && /^0+$/.test(digits);
}
